import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { UserDialogComponent } from './add-user-dialog/add-user-dialog.component'
import { Router } from '@angular/router'
import { ToastrModule, ToastrService } from 'ngx-toastr'
import { AuthService } from '../../auth.service'
import { AuthStateService } from '../../auth-state.service'
import { AddRoleDialogComponent as RoleDialogComponent } from './add-role-dialog/add-role-dialog.component'
import { UserService } from './user.service'
import { UserModel } from './user_model'
import { Claim } from './claim.model'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { ModulePermissionComponent } from './module-permission/module-permission.component'
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component'

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    ToastrModule,
    MatProgressBarModule,
    ModulePermissionComponent,
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.css',
})
export class UsersComponent implements OnInit {
  users: UserModel[] = []
  isLoading: boolean = false
  permissions: string[] = []
  isManagingRoles = false
  selectedUser: any = null
  constructor(
    private authService: AuthService,
    private authStateService: AuthStateService,
    private userService: UserService,
    public dialog: MatDialog,
    private router: Router,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.authService.getUsers().subscribe({
      next: (data) => {
        this.users = data
      },
      error: (error: any) => {
        this.isLoading = false
        this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
      },
    })
    this.authStateService.permissions.subscribe((permList) => {
      this.permissions = permList
    })
  }

  dashboardModule = {
    name: 'Dashboard',
    isEnabled: true,
    options: [],
  }

  quotationsModule = {
    name: 'Quotations',
    isEnabled: true,
    options: [
      { name: 'AddNew', isChecked: true },
      { name: 'PublishedList', isChecked: false },
      { name: 'DraftList', isChecked: false },
      { name: 'FollowUpList', isChecked: false },
      { name: 'ProformaList', isChecked: false },
      { name: 'IsAdmin', isChecked: false },
    ],
  }

  clientsModule = {
    name: 'Clients',
    isEnabled: true,
    options: [
      { name: 'AddNewClient', isChecked: false },
      { name: 'ClientList', isChecked: false },
    ],
  }

  membersModule = {
    name: 'Members',
    isEnabled: true,
    options: [
      { name: 'AddNewMember', isChecked: false },
      { name: 'MemberList', isChecked: false },
    ],
  }

  mastersModule = {
    name: 'Masters',
    isEnabled: true,
    options: [],
  }

  usersModule = {
    name: 'UserManagement',
    isEnabled: true,
    options: [],
  }

  vendorsModule = {
    name: 'Vendors',
    isEnabled: true,
    options: [
      { name: 'AddNewVendor', isChecked: false },
      { name: 'VendorList', isChecked: false },
    ],
  }

  vendorMembersModule = {
    name: 'VendorMembers',
    isEnabled: true,
    options: [
      { name: 'AddNewVendorMember', isChecked: false },
      { name: 'VendorMemberList', isChecked: false },
    ],
  }

  purchaseOrderModule = {
    name: 'PurchaseOrders',
    isEnabled: false,
    options: [
      { name: 'AddNew', isChecked: false },
      { name: 'View', isChecked: false },
      { name: 'IsAdmin', isChecked: false },
    ],
  }

  workOrderModule = {
    name: 'WorkOrders',
    isEnabled: false,
    options: [
      { name: 'AddNew', isChecked: false },
      { name: 'View', isChecked: false },
      { name: 'IsAdmin', isChecked: false },
      { name: 'PartNumberMaster', isChecked: false },
    ],
  }
  workOrderStatusModule = {
    name: 'WorkOrdersStatus',
    isEnabled: false,
    options: [
      { name: 'Invoice', isChecked: false },
      { name: 'Operations', isChecked: false },
      { name: 'Sticker', isChecked: false },
      { name: 'TC', isChecked: false },
    ],
  }

  initializePermissionsFromResponse(user: UserModel): void {
    this.userService.getClaimsForUser(user.username).subscribe({
      next: (response: any) => {
        if (response.isSuccess && Array.isArray(response.response)) {
          const receivedPermissions = response.response.map(
            (item: any) => item.value,
          )
          

          const modules = [
            this.quotationsModule,
            this.clientsModule,
            this.membersModule,
            this.mastersModule,
            this.usersModule,
            this.vendorsModule,
            this.vendorMembersModule,
            this.purchaseOrderModule,
            this.workOrderModule,
            this.workOrderStatusModule,
          ]

          // Iterate over each module
          modules.forEach((module) => {
            // Enable module-level access
            const moduleAccessKey = `${module.name.replace(/\s+/g, '')}Access`
            

            module.isEnabled = receivedPermissions.includes(moduleAccessKey)

            // Iterate through module options if present
            module.options.forEach((option: any) => {
              const permissionKey = `${module.name.replace(
                /\s+/g,
                '',
              )}${option.name.replace(/\s+/g, '')}Permission`
              option.isChecked = receivedPermissions.includes(permissionKey)
            })
          })
        } else {
          console.error('Invalid or empty permissions response:', response)
          this.toastr.error('Unable to fetch user permissions', 'Error')
        }
      },
      error: (error: any) => {
        console.error('Failed to fetch permissions:', error)
        this.toastr.error('Unable to fetch user permissions', 'Error')
      },
    })
  }

  updatePermissionsInDatabase(user: UserModel): void {
    const claims = this.getSelectedPermissions()
    
    this.isLoading = true
    this.userService.updatePermissionsForUser(user.username, claims).subscribe({
      next: () => {
        if (user.username == this.authService.getUserInfo()?.username) {
          this.authService.getPermissions(user.username).subscribe({
            next: (data) => {
              this.authStateService.setUserPermissions(data.permissions)
            },
            error: (error: any) => {
              this.isLoading = false
            },
          })
        }

        this.isLoading = false
        this.toastr.success(
          'Permissions Updated Successfully!!',
          'UpdateSuccess',
        )
      },
      error: (error: any) => {
        this.isLoading = false
        this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
      },
    })
  }

  handleToggle(module: any) {
    
    module.isEnabled = module.isEnabled === 'true'
    
    if (module.isEnabled === 'false') {
      // If the module is disabled, reset its permissions
      Object.keys(module.permissions).forEach((key) => {
        module.permissions[key] = false
      })
    } else {
      
    }
  }

  getSelectedPermissions(): Claim[] {
    const claims: Claim[] = []

    const modules = [
      this.quotationsModule,
      this.clientsModule,
      this.membersModule,
      this.mastersModule,
      this.usersModule,
      this.vendorsModule,
      this.vendorMembersModule,
      this.purchaseOrderModule,
      this.workOrderModule,
      this.workOrderStatusModule,
    ]

    modules.forEach((module) => {
      

      if (module.isEnabled) {
        claims.push({
          key: 'permissions',
          value: `${module.name.replace(/\s+/g, '')}Access`, // Remove spaces and add 'Access' suffix
        })
        // Add claims for each selected option in the module
        module.options.forEach((option: any) => {
          if (option.isChecked) {
            claims.push({
              key: 'permissions',
              value: `${module.name.replace(/\s+/g, '')}${option.name.replace(
                /\s+/g,
                '',
              )}Permission`,
            })
          }
        })
      }
    })
    

    return claims
  }

  openDialog(action: string, userData: any = null): void {
    let dialogRef = undefined

    if (action === 'user') {
      dialogRef = this.dialog.open(UserDialogComponent, {
        height: 'auto',
        width: '600px',
        data: userData ? { ...userData } : null,
      })
    } else {
      dialogRef = this.dialog.open(RoleDialogComponent, {
        height: 'auto',
        width: '600px',
        data: {},
      })
    }

    dialogRef?.afterClosed().subscribe((result) => {
      if (result != null && action === 'user') {
        this.authService.getUsers().subscribe({
          next: (data) => {
            this.users = data
            // this.toastr.success('User updated successfully!', 'Success')
          },
          error: (error: any) => {
            this.isLoading = false
            this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
          },
        })
      }
    })
  }

  confirmDelete(user: UserModel): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you want to delete this user?`,
      },
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteUser(user)
      }
    })
  }

  deleteUser(user: UserModel): void {
    this.userService.deleteUser(user.username).subscribe({
      next: () => {
        this.toastr.success('User deleted successfully', 'Success')
        this.authService.getUsers().subscribe({
          next: (data) => {
            this.users = data
          },
          error: (error: any) => {
            this.isLoading = false
            this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
          },
        })
      },
      error: (error: any) => {
        this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
      },
    })
  }

  manageRoles(user: any): void {
    this.isManagingRoles = true
    this.selectedUser = user
    this.initializePermissionsFromResponse(this.selectedUser)
  }

  closeManageRoles() {
    this.isManagingRoles = false
    this.selectedUser = null
  }
}
